"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.threeWheelMechanism = threeWheelMechanism;
const MPS_PER_MPH = 0.44704;
const SQRT3 = Math.sqrt(3);
const HALF_SQRT3 = Math.sqrt(3) / 2.0;
const TOP_GAIN = 2.0 / 3;
const SIDE_GAIN = -1.0 / 3;
const LATERAL_GAIN = 1.0 / Math.sqrt(3);
/** Stands in for "no limit" from a wheel that cannot bound the spin. */
const UNBOUNDED_SPIN = 1e6;
/**
 * Ball RPM per wheel RPM of spin. v1 converts by the ratio of the wheel's
 * diameter to the ball's, but uses the ball's diameter for both, so the
 * ratio is exactly 1; the conversions stay explicit so that a real ratio
 * changes only this.
 */
const BALL_SPIN_PER_WHEEL_SPIN = 1;
const toBallSpin = (wheelRpm) => BALL_SPIN_PER_WHEEL_SPIN * wheelRpm;
const toWheelSpin = (ballRpm) => ballRpm / BALL_SPIN_PER_WHEEL_SPIN;
const SPIN_LEVELS = 10;
const NO_SPIN = Object.freeze({ rpm: 0, axis: 0 });
const NO_CORRECTION = Object.freeze({ pitch: 0, yaw: 0 });
const STOPPED = Object.freeze({ top: 0, left: 0, right: 0 });
/** Angle of (x, y) in degrees, [0, 360), exactly as v1's vector2Angle. */
function axisAngle(x, y) {
    if (y === 0) {
        return x < 0 ? 180.0 : 0;
    }
    if (x === 0) {
        return y > 0 ? 90.0 : 270.0;
    }
    const angle = (Math.atan(Math.abs(y) / Math.abs(x)) * 180.0) / Math.PI;
    if (x > 0 && y < 0)
        return 360.0 - angle;
    if (x < 0 && y > 0)
        return 180.0 - angle;
    if (x < 0 && y < 0)
        return 180.0 + angle;
    return angle;
}
/** A wheel whose share of the spin is within this of zero cannot limit it. */
const snapToZero = (share) => (Math.abs(share) < 1.0e-8 ? 0 : share);
/**
 * The most spin (wheel RPM) about `axis` that keeps every wheel on its side
 * of `limitRpm`, given the wheels' mean RPM.
 */
function maxSpinWithin(axis, launchRpm, limitRpm) {
    if (launchRpm === limitRpm) {
        return 0;
    }
    const radians = axis * (Math.PI / 180.0);
    const top = snapToZero(Math.cos(radians));
    const left = snapToZero(Math.cos(radians) - SQRT3 * Math.sin(radians));
    const right = snapToZero(Math.cos(radians) + SQRT3 * Math.sin(radians));
    const headroom = launchRpm - limitRpm;
    const toward = Math.sign(headroom);
    return Math.min(toward * top < 0 ? -1.5 * headroom * (1.0 / top) : UNBOUNDED_SPIN, toward * left > 0 ? 3.0 * headroom * (1.0 / left) : UNBOUNDED_SPIN, toward * right > 0 ? 3.0 * headroom * (1.0 / right) : UNBOUNDED_SPIN);
}
/** Builds the mechanism. The spec must already be valid (profile loading checks it). */
function threeWheelMechanism(spec) {
    const { mphByRpm, minRpm, maxRpm, maxSpinRpm, speedDamping, spinDamping, deflection } = spec;
    const minSpeed = mphByRpm[0] * MPS_PER_MPH;
    const maxSpeed = mphByRpm[mphByRpm.length - 1] * MPS_PER_MPH;
    /** v1 mps2rpm: the band RPM whose speed is nearest, the lower on a tie. */
    const rpmForSpeed = (speed) => {
        if (!(speed >= 0)) {
            throw new RangeError(`launch speed ${speed} must be >= 0`);
        }
        const mph = Math.min(Math.max(speed, minSpeed), maxSpeed) / MPS_PER_MPH;
        let low = 0;
        let high = mphByRpm.length;
        while (low < high) {
            const mid = (low + high) >>> 1;
            if (mphByRpm[mid] < mph)
                low = mid + 1;
            else
                high = mid;
        }
        if (low === 0)
            return minRpm;
        if (low === mphByRpm.length)
            return maxRpm;
        const above = Math.abs(mph - mphByRpm[low]);
        const below = Math.abs(mph - mphByRpm[low - 1]);
        return minRpm + (above < below ? low : low - 1);
    };
    /** v1 rpm2mps: the speed at the nearest whole RPM, clamped to the band. */
    const speedForRpm = (rpm) => {
        if (!(rpm >= 0)) {
            throw new RangeError(`mean wheel RPM ${rpm} must be >= 0`);
        }
        const whole = Math.round(Math.min(Math.max(rpm, minRpm), maxRpm));
        return mphByRpm[whole - minRpm] * MPS_PER_MPH;
    };
    /** v1 maxmotorspin_for_rpmspeed: the most wheel spin about `axis` at a mean wheel RPM. */
    const maxWheelSpin = (axis, launchRpm) => {
        if (launchRpm === 0) {
            return 0;
        }
        if (!(axis >= 0 && axis <= 360)) {
            throw new RangeError(`spin axis ${axis} must be in [0, 360]`);
        }
        return Math.min(maxSpinWithin(axis, launchRpm, maxRpm), maxSpinWithin(axis, launchRpm, minRpm));
    };
    /** v1 spinlevel2motorspin: each level is the middle of its tenth of the spin range. */
    const wheelSpinByLevel = Object.freeze(Array.from({ length: SPIN_LEVELS + 1 }, (_, level) => level === 0 ? 0 : (level / 10.0 - 0.05) * maxSpinRpm));
    /** v1 motorspin2spinlevel: 0 only for no spin, else the tenth of the range it falls in. */
    const levelForWheelSpin = (wheelRpm) => {
        if (!(wheelRpm >= 0 && wheelRpm <= maxSpinRpm)) {
            throw new RangeError(`wheel spin ${wheelRpm} RPM must be in [0, ${maxSpinRpm}]`);
        }
        return wheelRpm === 0 ? 0 : Math.floor((wheelRpm / maxSpinRpm) * 9.999) + 1;
    };
    const spinLevels = Object.freeze({
        toRpm: (level) => {
            if (!(Number.isInteger(level) && level >= 0 && level <= SPIN_LEVELS)) {
                throw new RangeError(`spin level ${level} must be a whole number in [0, ${SPIN_LEVELS}]`);
            }
            return toBallSpin(wheelSpinByLevel[level]);
        },
        fromRpm: (rpm) => {
            if (!(rpm >= 0)) {
                throw new RangeError(`ball spin ${rpm} RPM must be >= 0`);
            }
            return levelForWheelSpin(toWheelSpin(rpm));
        },
        /**
         * v1 maxspinlevel: the level of the most spin, or the one below when
         * that level's own spin is more than the wheels can give. At some
         * speeds on the wheel-aligned axes the wheels can give a little more
         * than maxSpin, where v1 throws; that is level 10.
         */
        maxAt: (axis, speed) => {
            const most = Math.min(maxWheelSpin(axis, rpmForSpeed(speed)), maxSpinRpm);
            const level = levelForWheelSpin(most);
            return level > 0 && wheelSpinByLevel[level] > most ? level - 1 : level;
        },
    });
    const toMotors = (speed, spin) => {
        if (!(speed >= 0)) {
            throw new RangeError(`launch speed ${speed} must be >= 0`);
        }
        const hasSpin = spin.rpm > 0;
        const launchRpm = rpmForSpeed(hasSpin && speedDamping ? speed * speedDamping(spin.axis) : speed);
        if (launchRpm === 0) {
            return STOPPED;
        }
        if (!hasSpin) {
            return wheelRpms(launchRpm, 0, 0);
        }
        const achievable = Math.min(toWheelSpin(spin.rpm), maxWheelSpin(spin.axis, launchRpm));
        const spinRpm = spinDamping ? achievable * spinDamping(spin.axis) : achievable;
        return wheelRpms(launchRpm, Math.cos(spin.axis * (Math.PI / 180.0)) * spinRpm, Math.sin(spin.axis * (Math.PI / 180.0)) * spinRpm);
    };
    const fromMotors = (rpms) => {
        const { top, left, right } = rpms;
        const launchRpm = (top + left + right) / 3.0;
        const knuckleSpeed = speedForRpm(launchRpm);
        if (launchRpm === 0 || knuckleSpeed === 0) {
            return { speed: knuckleSpeed, spin: NO_SPIN };
        }
        const axis = axisAngle(top - 0.5 * left - 0.5 * right, HALF_SQRT3 * left - HALF_SQRT3 * right);
        const wheelSpin = Math.sqrt(0.5 * ((left - right) * (left - right) + (right - top) * (right - top) + (left - top) * (left - top)));
        return {
            speed: wheelSpin > 0 && speedDamping ? knuckleSpeed / speedDamping(axis) : knuckleSpeed,
            spin: { rpm: toBallSpin(spinDamping ? wheelSpin / spinDamping(axis) : wheelSpin), axis },
        };
    };
    const deflectionCorrection = (spin, speed) => {
        if (!(spin.rpm > 0 && deflection)) {
            return NO_CORRECTION;
        }
        const { pitchAdjust, yawAdjust } = deflection(spin.axis, speed);
        return { pitch: pitchAdjust, yaw: yawAdjust };
    };
    return Object.freeze({
        toMotors,
        fromMotors,
        deflectionCorrection,
        limits: Object.freeze({
            maxSpeed,
            minSpeed,
            maxSpin: toBallSpin(maxSpinRpm),
            rpm: Object.freeze({ min: minRpm, max: maxRpm, maxSpin: maxSpinRpm }),
        }),
        speedForRpm,
        rpmForSpeed,
        maxSpinAt: (axis, speed) => toBallSpin(maxWheelSpin(axis, rpmForSpeed(speed))),
        spinLevels,
    });
}
/** Wheel RPMs for a mean RPM and a spin vector (spinX, spinY) in wheel RPM. */
function wheelRpms(launchRpm, spinX, spinY) {
    return {
        top: TOP_GAIN * spinX + launchRpm,
        left: SIDE_GAIN * spinX + LATERAL_GAIN * spinY + launchRpm,
        right: SIDE_GAIN * spinX - LATERAL_GAIN * spinY + launchRpm,
    };
}
